import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Facebook, Instagram, Mail, Phone, MapPin,
  ShieldCheck, Lock, Send, Loader2, CheckCircle2,
} from "lucide-react";
import { useCompanyInfo } from "@/lib/company";
import { api } from "@/lib/api";

const LANGS = [
  { code: "fr", label: "FR" },
  { code: "nl", label: "NL" },
  { code: "en", label: "EN" },
  { code: "it", label: "IT" },
];

export default function Footer() {
  const { t, i18n } = useTranslation();
  const c = useCompanyInfo() || {};
  const year = c.copyright_year || new Date().getFullYear();
  const addr = c.address || {};
  const v = c.logo_version || 1;
  const currentLang = (i18n.resolvedLanguage || i18n.language || "fr").split("-")[0];

  return (
    <footer data-testid="site-footer" className="mt-20 border-t border-slate-200 bg-white">
      {/* V2.8 — Newsletter banner */}
      <NewsletterBanner />

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 grid gap-8 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2">
            <img src={`${c.logo_horizontal_url || "/branding/solarsim-logo-horizontal.png"}?v=${v}`} alt={c.brand_name || "SolarSim"} loading="lazy" className="h-20 w-auto md:h-24 object-contain" />
          </div>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed">{t("footer.tagline")}</p>
          <div className="mt-4 flex gap-3">
            {c.socials?.facebook && (
              <a href={c.socials.facebook} target="_blank" rel="noreferrer" data-testid="footer-social-facebook"
                 className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-solar-green hover:text-white transition-colors">
                <Facebook className="h-4 w-4" strokeWidth={2} />
              </a>
            )}
            {c.socials?.instagram && (
              <a href={c.socials.instagram} target="_blank" rel="noreferrer" data-testid="footer-social-instagram"
                 className="grid h-8 w-8 place-items-center rounded-full bg-slate-100 text-slate-600 hover:bg-solar-green hover:text-white transition-colors">
                <Instagram className="h-4 w-4" strokeWidth={2} />
              </a>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">{t("footer.company")}</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="text-slate-600 hover:text-solar-green" data-testid="footer-link-about">{t("footer.about")}</Link></li>
            <li><Link to="/contact" className="text-slate-600 hover:text-solar-green" data-testid="footer-link-contact">{t("footer.contact")}</Link></li>
            <li><Link to="/reviews" className="text-slate-600 hover:text-solar-green" data-testid="footer-link-reviews">{t("footer.reviews")}</Link></li>
            <li><Link to="/simulateur" className="text-slate-600 hover:text-solar-green">{t("nav.simulator")}</Link></li>
          </ul>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">{t("footer.legal")}</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="text-slate-600 hover:text-solar-green" data-testid="footer-link-privacy">{t("footer.privacy")}</Link></li>
            <li><Link to="/cookies" className="text-slate-600 hover:text-solar-green" data-testid="footer-link-cookies">{t("footer.cookies")}</Link></li>
            <li><Link to="/terms" className="text-slate-600 hover:text-solar-green" data-testid="footer-link-terms">{t("footer.terms")}</Link></li>
          </ul>
          {/* V2.8 — Language selector */}
          <div className="mt-6" data-testid="footer-lang-selector">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">
              {t("footer.language_switch_label")}
            </p>
            <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5">
              {LANGS.map((l) => {
                const active = currentLang === l.code;
                return (
                  <button
                    key={l.code}
                    type="button"
                    data-testid={`footer-lang-${l.code}`}
                    data-active={active}
                    onClick={() => i18n.changeLanguage(l.code)}
                    className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
                      active
                        ? "bg-emerald-500 text-white shadow-sm"
                        : "text-slate-400 hover:text-emerald-500"
                    }`}
                  >
                    {l.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">{t("footer.reach_us")}</h3>
          <ul className="space-y-2 text-sm text-slate-600">
            {c.email && (
              <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 text-solar-green flex-none" /><a href={`mailto:${c.email}`} data-testid="footer-email" className="hover:text-solar-green break-all">{c.email}</a></li>
            )}
            {c.phones?.map((phone, i) => (
              <li key={phone} className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 text-solar-green flex-none" /><a href={`tel:${phone.replace(/\s/g, "")}`} data-testid={i === 0 ? "footer-phone" : `footer-phone-${i}`} className="hover:text-solar-green">{phone}</a></li>
            ))}
            {(addr.street || addr.city) && (
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-solar-green flex-none" /><span data-testid="footer-address">{addr.street}<br/>{addr.postal_code} {addr.city}</span></li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs text-slate-500">
          <span data-testid="footer-copy">
            © {year} {c.legal_name || "SolarSim"} · KVK <span data-testid="footer-kvk">{c.kvk || "—"}</span>
            {` · ${t("footer.vat_label", "BTW")} `}<span data-testid="footer-vat">{c.vat || "—"}</span>
          </span>
          <div className="flex flex-wrap items-center gap-2" data-testid="footer-compliance-badges">
            <ComplianceBadge
              testId="footer-badge-eidas"
              icon={ShieldCheck}
              label="eIDAS"
              tooltip={t("footer.badge_eidas_tooltip")}
              color="emerald"
            />
            <ComplianceBadge
              testId="footer-badge-rgpd"
              icon={Lock}
              label={t("footer.badge_rgpd_label")}
              tooltip={t("footer.badge_rgpd_tooltip")}
              color="emerald"
            />
            <ComplianceBadge
              testId="footer-badge-ssl"
              icon={ShieldCheck}
              label="SSL"
              tooltip={t("footer.badge_ssl_tooltip")}
              color="teal"
            />
            <span className="text-slate-400 ml-2 hidden sm:inline">·</span>
            <span className="text-slate-400 hidden sm:inline">{t("footer.pvgis_note")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function ComplianceBadge({ testId, icon: Icon, label, tooltip, color }) {
  const cls =
    color === "teal"
      ? "border-teal-500/40 bg-teal-500/10 text-teal-700"
      : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
  return (
    <span
      data-testid={testId}
      title={tooltip}
      aria-label={tooltip}
      className={`inline-flex items-center gap-1 rounded-md border ${cls} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide`}
    >
      <Icon className="h-3 w-3" strokeWidth={2.5} />
      {label}
    </span>
  );
}

function NewsletterBanner() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | already | error
  const [message, setMessage] = useState("");

  const submit = async (ev) => {
    ev.preventDefault();
    if (status === "loading") return;
    const trimmed = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setStatus("error");
      setMessage(t("newsletter.email_invalid"));
      return;
    }
    setStatus("loading");
    setMessage("");
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: trimmed,
        locale: (i18n.resolvedLanguage || i18n.language || "fr").split("-")[0],
        source_page: location.pathname,
      });
      if (data.status === "already_subscribed") {
        setStatus("already");
        setMessage(t("newsletter.already_subscribed"));
      } else if (data.status === "pending_confirmation") {
        setStatus("ok");
        setMessage(t("newsletter.pending_confirmation", { email: trimmed }));
      } else {
        setStatus("ok");
        setMessage(t("newsletter.success"));
      }
    } catch (err) {
      const code = err?.response?.status;
      setStatus("error");
      setMessage(
        code === 429
          ? t("newsletter.rate_limited")
          : err?.response?.data?.detail || t("newsletter.error")
      );
    }
  };

  const done = status === "ok" || status === "already";

  return (
    <section
      data-testid="footer-newsletter"
      className="border-t border-b border-emerald-500/20 bg-emerald-500/5"
    >
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[1.2fr_1fr] lg:items-center lg:px-8 lg:py-10">
        <div>
          <h3 className="text-xl font-semibold tracking-tight text-solar-slate sm:text-2xl">
            {t("newsletter.title")}
          </h3>
          <p className="mt-1 text-sm text-slate-600">{t("newsletter.subtitle")}</p>
        </div>
        {done ? (
          <div
            data-testid="footer-newsletter-success"
            className="inline-flex items-start gap-2 rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-emerald-700"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" strokeWidth={2.2} />
            <span>{message}</span>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row" data-testid="footer-newsletter-form">
            <label className="relative flex-1">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("newsletter.email_placeholder")}
                data-testid="footer-newsletter-email"
                className="w-full rounded-full border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm text-solar-slate placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
            </label>
            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="footer-newsletter-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-emerald-600 disabled:bg-emerald-300"
            >
              {status === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" strokeWidth={2.2} />
              )}
              {t("newsletter.submit")}
            </button>
            {status === "error" && (
              <p
                data-testid="footer-newsletter-error"
                className="mt-1 basis-full text-xs text-red-600 sm:mt-0"
              >
                {message}
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
